import { ReactElement, useState, useMemo } from "react";
import { PageContainer, PageTitle, StatCard, LoyaltyCardDialog, SkeletonLoader } from "../../global/Common";
import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { useLanguage } from "../../context/LanguageContext";
import { useSettings } from "../../context/SettingsContext";
import { useBrand } from "../../context/BrandContext";
import { useCompanyConfigContext } from "../../context/CompanyConfigContext";
import { useDashboardData } from "../../hooks/useDashboardData";
import OfferBarcodeDialog from "../MesOffres/OfferBarcodeDialog";
import { Offer, SelectedOffer } from "../MesOffres/types";
import { getOfferImage } from "../MesOffres/utils";
import { toDisplayName } from "../../utils/text";
import { LogoCard } from "./LogoCard";
import { UserInfoCard } from "./UserInfoCard";
import { StoreInfo } from "./StoreInfo";
import { OffersCarousel } from "./OffersCarousel";

const Dashboard = (): ReactElement => {
  const { t, language } = useLanguage();
  const { isDiamondsEnabled } = useSettings();
  const { brand } = useBrand();
  const { loading: configLoading, configs } = useCompanyConfigContext();
  const { person, offers, store, cardIdentifier, totalPoints, totalDiamonds, loading } = useDashboardData();
  
  const getConfigValue = (key: string, defaultValue: string = ''): string => {
    if (!configLoading && Object.keys(configs).length > 0 && configs[key]) {
      const value = configs[key];
      return typeof value === 'string' ? value : defaultValue;
    }
    return defaultValue;
  };

  const baseline = useMemo(() => {
    const configBaseline = getConfigValue('baseline');
    return configBaseline || brand.welcomeMessage[language as keyof typeof brand.welcomeMessage] || brand.welcomeMessage.fr;
  }, [configLoading, configs, brand.welcomeMessage, language]);

  const dashboardImageUrl = useMemo(() => {
    const imageUrl = getConfigValue('dashboard.image.url');
    return imageUrl || undefined;
  }, [configLoading, configs]);

  const primaryColor = useMemo(() => {
    const color = getConfigValue('css.main.color', brand.primaryColor);
    return color.startsWith('#') ? color : `#${color}`;
  }, [configLoading, configs, brand.primaryColor]);
  
  const [openCardDialog, setOpenCardDialog] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<SelectedOffer | null>(null);

  const handleOfferClick = (offer: Offer) => {
    if (offer.offerNumber) {
      setSelectedOffer({
        offerNumber: offer.offerNumber,
        offerName: offer.offerName || t('pages.offers.noTitle'),
        endDate: offer.endDate
      });
    }
  };

  if (configLoading) {
    return (
      <PageContainer>
        <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />
        
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: isDiamondsEnabled ? '2fr 1fr 1fr' : '1fr 1fr' }, 
          gap: 2,
          mb: 3
        }}>
          <Card>
            <CardContent>
              <SkeletonLoader count={4} />
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <SkeletonLoader count={3} />
            </CardContent>
          </Card>
          {isDiamondsEnabled && (
            <Card>
              <CardContent>
                <SkeletonLoader count={3} />
              </CardContent>
            </Card>
          )}
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <SkeletonLoader count={6} />
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <SkeletonLoader count={4} />
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>{baseline}</PageTitle>

      {!dashboardImageUrl && (
        <LogoCard 
          logoUrl={brand.logoCard}
          displayName={brand.displayName}
          primaryColor={primaryColor}
        />
      )}

      {dashboardImageUrl && (
        <LogoCard 
          displayName={brand.displayName}
          primaryColor={primaryColor}
          dashboardImageUrl={dashboardImageUrl}
        />
      )}

      {!loading && person && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#495057', textAlign: 'center' }}>
          {t('pages.dashboard.greeting')} {person?.firstName ? toDisplayName(person.firstName) : ''} {person?.lastName ? toDisplayName(person.lastName) : ''}
        </Typography>
      )}

      <OffersCarousel
        offers={offers}
        loading={loading}
        primaryColor={primaryColor}
        onOfferClick={handleOfferClick}
        t={t}
      />

      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.25rem' }}>
          <i className="bx bx-star" style={{ fontSize: 24, color: primaryColor }} />
          {t('pages.dashboard.myPoints')}
        </Box>
        <StatCard 
          icon="bx bx-star" 
          value={totalPoints} 
          label={t('pages.dashboard.loyaltyPoints')}
          loading={loading}
          animationDelay={0}
        />
      </Box>

      {isDiamondsEnabled && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1.25rem' }}>
            <i className="bx bx-diamond" style={{ fontSize: 24, color: primaryColor }} />
            {t('pages.dashboard.myDiamonds')}
          </Box>
          <StatCard 
            icon="bx bx-diamond" 
            value={totalDiamonds} 
            label={t('pages.dashboard.diamonds')}
            loading={loading}
            animationDelay={500}
            showProgressBar={true}
            maxValue={100}
          />
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <UserInfoCard
          person={person}
          cardIdentifier={cardIdentifier}
          loading={loading}
          primaryColor={primaryColor}
          onCardClick={() => setOpenCardDialog(true)}
          t={t}
        />
      </Box>

      <StoreInfo 
        store={store}
        loading={loading}
        primaryColor={primaryColor}
        t={t}
      />

      <LoyaltyCardDialog 
        open={openCardDialog} 
        onClose={() => setOpenCardDialog(false)}
        personData={{ PersonResponse: { PersonResponse: person, IdentifierResponse: [{ identifierValue: cardIdentifier }] } }}
        loading={loading}
      />

      {selectedOffer && (
        <OfferBarcodeDialog
          open={!!selectedOffer}
          onClose={() => setSelectedOffer(null)}
          offerNumber={selectedOffer.offerNumber}
          offerName={selectedOffer.offerName}
          endDate={selectedOffer.endDate}
          offerImage={getOfferImage(selectedOffer.offerNumber)}
        />
      )}

    </PageContainer>
  );
};

export default Dashboard;
